import { Check, HeartHandshake, Repeat2, Users } from 'lucide-react'

import { ButtonLink } from '@/components/ButtonLink'
import { DonationPanel } from '@/components/DonationPanel'
import { InnerHero } from '@/components/InnerHero'
import { Reveal } from '@/components/Reveal'
import { SectionIntro } from '@/components/SectionIntro'
import { donationUrl } from '@/data/site'

const communityValues = [
  {
    icon: Repeat2,
    title: 'Constancia',
    description: 'Un apoyo recurrente permite organizar tratamientos y cuidados con mayor previsión.',
  },
  {
    icon: HeartHandshake,
    title: 'Cercanía',
    description: 'La comunidad acompaña con empatía, sin invadir y con disposición para escuchar.',
  },
  {
    icon: Users,
    title: 'Familia elegida',
    description: 'Son quienes no tienen obligación de estar, pero deciden hacerlo por amor.',
  },
]

const commitments = [
  'Aportar con la periodicidad que resulte sostenible.',
  'Compartir campañas y necesidades específicas cuando sea posible.',
  'Celebrar avances y sostener los momentos difíciles.',
  'Invitar a otras personas a conocer el movimiento.',
]

export function CommunityPage() {
  return (
    <>
      <InnerHero
        eyebrow="Una familia que se elige"
        title="Los Tíos Chidos"
        description="Personas que aman, acompañan y consienten sin obligación. Una red cercana que decide sostener el camino de Noa."
      />

      <section className="section-space pt-0" aria-labelledby="community-intro-title">
        <div className="container-wide story-grid">
          <Reveal>
            <img
              className="aspect-[4/5] w-full rounded-[3rem] object-cover shadow-soft"
              src="/assets/images/noa-playa.webp"
              alt="Noa sentada en la arena con un sombrero grande"
              width="1200"
              height="1600"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="eyebrow">La comunidad mensual</p>
            <h2 className="section-title" id="community-intro-title">
              Estar una vez ayuda. Estar cada mes transforma.
            </h2>
            <div className="body-copy mt-8">
              <p>
                La rehabilitación neurológica puede requerir terapias frecuentes,
                equipo especializado y cuidados cotidianos durante mucho tiempo. Para
                una familia, esa combinación también significa una presión económica
                constante.
              </p>
              <p>
                Los Tíos Chidos convierten pequeñas aportaciones recurrentes en una
                base más predecible. Eso permite dedicar menos energía a organizar
                campañas urgentes y más al cuidado y la recuperación.
              </p>
            </div>
            <div className="mt-8">
              <ButtonLink external href={donationUrl} variant="primary">
                Quiero ser Tío Chido
              </ButtonLink>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-space bg-lilac-soft" aria-labelledby="community-values-title">
        <div className="container-wide">
          <SectionIntro
            align="center"
            eyebrow="Lo que significa"
            title="Una forma sencilla de decir: aquí estoy."
          />
          <div className="impact-grid">
            {communityValues.map((value, index) => {
              const Icon = value.icon

              return (
                <Reveal delay={index * 0.1} key={value.title}>
                  <article className="impact-card">
                    <span className="impact-index">0{index + 1}</span>
                    <span className="impact-icon">
                      <Icon aria-hidden="true" size={32} strokeWidth={1.5} />
                    </span>
                    <h3>{value.title}</h3>
                    <p>{value.description}</p>
                  </article>
                </Reveal>
              )
            })}
          </div>
        </div>
      </section>

      <section className="section-space" aria-labelledby="commitment-title">
        <div className="container-copy">
          <Reveal>
            <p className="eyebrow">Un acuerdo desde el corazón</p>
            <h2 className="section-title" id="commitment-title">
              Cada persona participa a su manera.
            </h2>
            <div className="mt-10 grid gap-3">
              {commitments.map((commitment) => (
                <div className="glass-card flex items-start gap-4 p-5" key={commitment}>
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-lime">
                    <Check aria-hidden="true" size={18} strokeWidth={2.6} />
                  </span>
                  <p className="m-0 pt-1 leading-relaxed">{commitment}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <DonationPanel />
    </>
  )
}
